import { Flag } from 'lucide-react'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useMarkSignalFalsePositive } from '@/api/signals.hook'

import { useTrackCallback } from '@/lib/analytics/events'
import { useReportErrorsCallback } from '@/lib/connect/error-reporting'

import { REASON_FIELD_ID } from '@/pages/alert-details/consts'
import {
  FALSE_POSITIVE_MODAL_TEXT,
  SIGNAL_FLAGGED_SUCCESS_LABEL,
  SIGNAL_FLAG_FALSE_POSITIVE_FAILED_LABEL,
} from '@/pages/alert-details/labels'

import { FormControl } from '@/components/lemmala-design-system/form-control/form-control'
import { Modal } from '@/components/lemmala-design-system/modal/modal'
import { Textarea } from '@/components/lemmala-design-system/textarea/textarea'
import { useToast } from '@/components/lemmala-design-system/toast/use-toast'

type FalsePositiveModalProps = {
  title?: string
  isOpen: boolean
  onClose: (isClosed?: boolean) => void
  signalId: string
  signalTitle: string
  companyName?: string
}

export const FalsePositiveModal = ({
  title = FALSE_POSITIVE_MODAL_TEXT.TITLE,
  isOpen,
  onClose,
  signalId,
  signalTitle,
  companyName,
}: FalsePositiveModalProps) => {
  const { mutateAsync: markFalsePositive, isPending } = useMarkSignalFalsePositive(signalId)
  const { toast } = useToast()
  const trackUpdate = useTrackCallback('signal.false-positive.mark')
  const reportErrors = useReportErrorsCallback()
  const [comment, setComment] = useState('')
  const location = useLocation()

  const resetAndClose = (success?: boolean) => {
    setComment('')
    onClose(success)
  }

  const handleUpdate = async () => {
    try {
      trackUpdate({
        signalId,
        isFalsePositive: true,
        comment,
        from: location.pathname,
        signal: signalTitle,
        company: companyName,
      })
      await markFalsePositive({
        id: signalId,
        isFalsePositive: true,
        reason: comment || undefined,
      })
      toast({
        variant: 'success',
        title: SIGNAL_FLAGGED_SUCCESS_LABEL,
      })
      resetAndClose(true)
    } catch (e) {
      reportErrors(e as Error)
      toast({
        variant: 'destructive',
        title: SIGNAL_FLAG_FALSE_POSITIVE_FAILED_LABEL,
      })
    }
  }

  return (
    <Modal
      open={isOpen}
      title={title}
      size='small'
      variant='destructive'
      primaryButtonText={FALSE_POSITIVE_MODAL_TEXT.PRIMARY_BUTTON}
      primaryButtonIcon={<Flag strokeWidth={1.5} className='size-4' />}
      confirmLoading={isPending}
      maskClosable={!isPending}
      keyboard={!isPending}
      cancelButtonProps={{ variant: 'outline' }}
      onCancel={() => resetAndClose(false)}
      onOk={handleUpdate}
      subtitle={FALSE_POSITIVE_MODAL_TEXT.SUBTITLE}
    >
      <FormControl label={FALSE_POSITIVE_MODAL_TEXT.REASON_LABEL} htmlFor={REASON_FIELD_ID}>
        <Textarea
          id={REASON_FIELD_ID}
          placeholder={FALSE_POSITIVE_MODAL_TEXT.REASON_PLACEHOLDER}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </FormControl>
    </Modal>
  )
}
